import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import './UserDataTable.css';

interface User {
  id: number;
  name: string;
  email: string;
  phone: string;
  role: 'admin' | 'central_committee' | 'building_committee' | 'manager' | 'resident' | 'visitor';
  flat_no: string | null;
  building_id: number | null;
  is_active: boolean;
  profile_picture?: string;
  created_at: string;
  updated_at: string;
  building?: {
    id: number;
    name: string;
  };
  ownerships?: {
    id: number;
    flat_number: string;
    building: {
      id: number;
      name: string;
    };
  }[];
}

interface UserFormData {
  name: string;
  email: string;
  phone: string;
  password: string;
  role: 'admin' | 'central_committee' | 'building_committee' | 'manager' | 'resident' | 'visitor';
  building_id: number | null;
  flat_no: string;
  is_active: boolean;
}

interface PaginationData {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  from: number;
  to: number;
  data: User[];
}

const UserDataTable: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Pagination state
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0,
    from: 0,
    to: 0
  });
  
  // Filters
  const [filters, setFilters] = useState({
    search: '',
    role: 'all',
    status: 'all',
    building_id: 'all'
  });
  
  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [userForm, setUserForm] = useState<UserFormData>({
    name: '',
    email: '',
    phone: '',
    password: '',
    role: 'resident',
    building_id: null,
    flat_no: '',
    is_active: true,
  });
  
  const [buildings, setBuildings] = useState<{id: number; name: string; total_floors: number; flats_per_floor: number}[]>([]);
  const [selectedBuilding, setSelectedBuilding] = useState<{id: number; name: string; total_floors: number; flats_per_floor: number} | null>(null);

  // Fetch users with server-side pagination and filtering
  const fetchUsers = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: pagination.per_page.toString(),
      });
      
      // Add filters
      if (filters.search) params.append('search', filters.search);
      if (filters.role !== 'all') params.append('role', filters.role);
      if (filters.status !== 'all') params.append('is_active', filters.status === 'active' ? 'true' : 'false');
      if (filters.building_id !== 'all') params.append('building_id', filters.building_id);
      
      const response = await api.get(`/users?${params.toString()}`);
      
      if (response.data.success) {
        const paginationData: PaginationData = response.data.data;
        setUsers(paginationData.data);
        setPagination({
          current_page: paginationData.current_page,
          last_page: paginationData.last_page,
          per_page: paginationData.per_page,
          total: paginationData.total,
          from: paginationData.from,
          to: paginationData.to
        });
      } else {
        setError(response.data.message || 'Failed to fetch users');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'An error occurred while fetching users');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.per_page]);

  // Fetch buildings for dropdown
  const fetchBuildings = useCallback(async () => {
    try {
      const response = await api.get('/public/buildings');
      if (response.data.success) {
        setBuildings(response.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch buildings:', err);
    }
  }, []);

  // Generate floors based on building's total_floors
  const generateFloors = useCallback((totalFloors: number) => {
    const floors = [];
    for (let i = 1; i <= totalFloors; i++) {
      floors.push({ id: i, floor_number: i });
    }
    return floors;
  }, []);

  // Generate flats based on building's flats_per_floor
  const generateFlats = useCallback((flatsPerFloor: number) => {
    const flatLetters = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P'];
    const flats = [];
    for (let i = 0; i < flatsPerFloor && i < flatLetters.length; i++) {
      flats.push({ id: i, flat_letter: flatLetters[i] });
    }
    return flats;
  }, []);

  useEffect(() => {
    fetchUsers(1);
    fetchBuildings();
  }, [fetchUsers, fetchBuildings]);

  // When building changes (in form), set selected building
  useEffect(() => {
    if (userForm.building_id) {
      const building = buildings.find(b => b.id === userForm.building_id);
      setSelectedBuilding(building || null);
    } else {
      setSelectedBuilding(null);
    }
  }, [userForm.building_id, buildings]);

  // Handle filter changes
  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  // Apply non-search filters immediately (reset to page 1)
  useEffect(() => {
    fetchUsers(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.role, filters.status, filters.building_id, pagination.per_page]);

  // Debounce search to avoid excessive requests
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchUsers(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [filters.search, fetchUsers]);

  // Handle page change
  const handlePageChange = (page: number) => {
    fetchUsers(page);
  };

  // Handle per page change
  const handlePerPageChange = (perPage: number) => {
    setPagination(prev => ({ ...prev, per_page: perPage, current_page: 1 }));
    fetchUsers(1);
  };

  // Reset all filters
  const handleResetFilters = () => {
    setFilters({ search: '', role: 'all', status: 'all', building_id: 'all' });
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  // Handle user creation/update
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const formData = { ...userForm };
      if (!editingUser && !formData.password) {
        setError('Password is required for new users');
        setLoading(false);
        return;
      }

      let response;
      if (editingUser) {
        response = await api.put(`/users/${editingUser.id}`, formData);
      } else {
        response = await api.post('/users', formData);
      }

      if (response.data.success) {
        setSuccess(editingUser ? 'User updated successfully' : 'User created successfully');
        setShowForm(false);
        setEditingUser(null);
        setUserForm({
          name: '',
          email: '',
          phone: '',
          password: '',
          role: 'resident',
          building_id: null,
          flat_no: '',
          is_active: true,
        });
        fetchUsers(pagination.current_page);
      } else {
        setError(response.data.message || 'Failed to save user');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'An error occurred while saving user');
    } finally {
      setLoading(false);
    }
  };

  // Handle user edit
  const handleEdit = (user: User) => {
    setEditingUser(user);
    setUserForm({
      name: user.name,
      email: user.email,
      phone: user.phone,
      password: '',
      role: user.role,
      building_id: user.building_id,
      flat_no: user.flat_no || '',
      is_active: user.is_active,
    });
    setShowForm(true);
  };

  // Handle user delete
  const handleDelete = async (userId: number) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    setLoading(true);
    try {
      const response = await api.delete(`/users/${userId}`);
      if (response.data.success) {
        setSuccess('User deleted successfully');
        fetchUsers(pagination.current_page);
      } else {
        setError(response.data.message || 'Failed to delete user');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'An error occurred while deleting user');
    } finally {
      setLoading(false);
    }
  };

  // Handle status toggle
  const handleToggleStatus = async (user: User) => {
    setLoading(true);
    try {
      const response = await api.post(`/users/${user.id}/toggle-status`);
      if (response.data.success) {
        setSuccess(`User ${user.is_active ? 'deactivated' : 'activated'} successfully`);
        fetchUsers(pagination.current_page);
      } else {
        setError(response.data.message || 'Failed to update user status');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'An error occurred while updating user status');
    } finally {
      setLoading(false);
    }
  };

  // Get role color
  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'danger';
      case 'central_committee': return 'primary';
      case 'building_committee': return 'info';
      case 'manager': return 'warning';
      case 'resident': return 'success';
      case 'visitor': return 'secondary';
      default: return 'secondary';
    }
  };

  // Render pagination
  const renderPagination = () => {
    const pages = [];
    const startPage = Math.max(1, pagination.current_page - 2);
    const endPage = Math.min(pagination.last_page, pagination.current_page + 2);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`pagination-btn ${i === pagination.current_page ? 'active' : ''}`}
          onClick={() => handlePageChange(i)}
          disabled={loading}
        >
          {i}
        </button>
      );
    }

    return (
      <div className="pagination">
        <button
          className="pagination-btn"
          onClick={() => handlePageChange(pagination.current_page - 1)}
          disabled={pagination.current_page === 1 || loading}
        >
          Previous
        </button>
        {pages}
        <button
          className="pagination-btn"
          onClick={() => handlePageChange(pagination.current_page + 1)}
          disabled={pagination.current_page === pagination.last_page || loading}
        >
          Next
        </button>
      </div>
    );
  };

  if (showForm) {
    return (
      <div className="user-data-table">
        <div className="table-header">
          <h2>{editingUser ? 'Edit User' : 'Add New User'}</h2>
          <button
            className="btn btn-secondary"
            onClick={() => {
              setShowForm(false);
              setEditingUser(null);
              setUserForm({
                name: '',
                email: '',
                phone: '',
                password: '',
                role: 'resident',
                building_id: null,
                flat_no: '',
                is_active: true,
              });
            }}
          >
            Back to List
          </button>
        </div>

        <form onSubmit={handleSubmit} className="user-form">
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="name">Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={userForm.name}
                onChange={(e) => setUserForm(prev => ({ ...prev, name: e.target.value }))}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={userForm.email}
                onChange={(e) => setUserForm(prev => ({ ...prev, email: e.target.value }))}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone">Phone *</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={userForm.phone}
                onChange={(e) => setUserForm(prev => ({ ...prev, phone: e.target.value }))}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password {!editingUser && '*'}</label>
              <input
                type="password"
                id="password"
                name="password"
                value={userForm.password}
                onChange={(e) => setUserForm(prev => ({ ...prev, password: e.target.value }))}
                required={!editingUser}
              />
            </div>

            <div className="form-group">
              <label htmlFor="role">Role *</label>
              <select
                id="role"
                name="role"
                value={userForm.role}
                onChange={(e) => setUserForm(prev => ({ ...prev, role: e.target.value as any }))}
                required
              >
                <option value="resident">Resident</option>
                <option value="manager">Manager</option>
                <option value="building_committee">Building Committee</option>
                <option value="central_committee">Central Committee</option>
                <option value="admin">Admin</option>
                <option value="visitor">Visitor</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="building_id">Building</label>
              <select
                id="building_id"
                name="building_id"
                value={userForm.building_id || ''}
                onChange={(e) => {
                  const val = e.target.value ? parseInt(e.target.value) : null;
                  setUserForm(prev => ({ ...prev, building_id: val, flat_no: '' }));
                }}
              >
                <option value="">Select Building</option>
                {buildings.map(building => (
                  <option key={building.id} value={building.id}>{building.name}</option>
                ))}
              </select>
            </div>

            {selectedBuilding && (
              <div className="form-group">
                <label htmlFor="floor">Floor</label>
                <select
                  id="floor"
                  value={(userForm.flat_no || '').split('-')[0] || ''}
                  onChange={(e) => {
                    const floorNum = e.target.value;
                    // reset flat selection, keep floor prefix
                    setUserForm(prev => ({ ...prev, flat_no: floorNum ? `${floorNum}-` : '' }));
                  }}
                >
                  <option value="">Select Floor</option>
                  {generateFloors(selectedBuilding.total_floors).map(f => (
                    <option key={f.id} value={f.floor_number}>{f.floor_number}</option>
                  ))}
                </select>
              </div>
            )}

            {selectedBuilding && (
              <div className="form-group">
                <label htmlFor="flat">Flat</label>
                <select
                  id="flat"
                  value={(userForm.flat_no || '').split('-')[1] || ''}
                  onChange={(e) => {
                    const letter = e.target.value;
                    const floorPart = (userForm.flat_no || '').split('-')[0] || '';
                    const combined = floorPart && letter ? `${floorPart}-${letter}` : (floorPart || '');
                    setUserForm(prev => ({ ...prev, flat_no: combined }));
                  }}
                >
                  <option value="">Select Flat</option>
                  {generateFlats(selectedBuilding.flats_per_floor).map(fl => (
                    <option key={fl.id} value={fl.flat_letter}>{fl.flat_letter}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={userForm.is_active}
                  onChange={(e) => setUserForm(prev => ({ ...prev, is_active: e.target.checked }))}
                />
                Active User
              </label>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : (editingUser ? 'Update User' : 'Create User')}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="user-data-table">
      <div className="table-header">
        <h2>User Management</h2>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>
          <i className="fas fa-plus"></i> Add User
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          <i className="fas fa-exclamation-circle"></i>
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <i className="fas fa-check-circle"></i>
          {success}
        </div>
      )}

      <div className="table-filters">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search users..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
          />
          <button className="search-btn" onClick={() => fetchUsers(1)} title="Search">
            <i className="fas fa-search"></i>
          </button>
        </div>

        <div className="filter-group">
          <select
            value={filters.role}
            onChange={(e) => handleFilterChange('role', e.target.value)}
          >
            <option value="all">All Roles</option>
            <option value="admin">Admin</option>
            <option value="central_committee">Central Committee</option>
            <option value="building_committee">Building Committee</option>
            <option value="manager">Manager</option>
            <option value="resident">Resident</option>
            <option value="visitor">Visitor</option>
          </select>

          <select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          <select
            value={filters.building_id}
            onChange={(e) => handleFilterChange('building_id', e.target.value)}
          >
            <option value="all">All Buildings</option>
            {buildings.map(building => (
              <option key={building.id} value={building.id.toString()}>{building.name}</option>
            ))}
          </select>

          <select
            value={pagination.per_page}
            onChange={(e) => handlePerPageChange(parseInt(e.target.value))}
          >
            <option value={10}>10 per page</option>
            <option value={15}>15 per page</option>
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
          </select>

          <button className="btn btn-secondary" onClick={handleResetFilters} title="Reset filters">
            <i className="fas fa-rotate"></i>
          </button>
        </div>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Role</th>
              <th>Building</th>
              <th>Flat</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} className="loading-cell">
                  <div className="loading-spinner">
                    <i className="fas fa-spinner fa-spin"></i>
                    Loading users...
                  </div>
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={8} className="empty-cell">
                  <div className="empty-state">
                    <i className="fas fa-users"></i>
                    <h3>No users found</h3>
                    <p>Try adjusting your search or filter criteria</p>
                  </div>
                </td>
              </tr>
            ) : (
              users.map(user => (
                <tr key={user.id}>
                  <td>
                    <div className="user-info">
                      <div className="user-avatar">
                        {user.profile_picture ? (
                          <img 
                            src={user.profile_picture} 
                            alt={user.name}
                            className="avatar-image"
                          />
                        ) : (
                          <i className="fas fa-user"></i>
                        )}
                      </div>
                      <div>
                        <div className="user-name">{user.name}</div>
                        <div className="user-id">ID: {user.id}</div>
                      </div>
                    </div>
                  </td>
                  <td>{user.email}</td>
                  <td>{user.phone}</td>
                  <td>
                    <span className={`badge badge-${getRoleColor(user.role)}`}>
                      {user.role.replace('_', ' ')}
                    </span>
                  </td>
                  <td>{user.building?.name || '-'}</td>
                  <td>{user.flat_no || '-'}</td>
                  <td>
                    <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      {user.role !== 'admin' && (
                        <button
                          className="btn btn-sm btn-info"
                          onClick={() => handleEdit(user)}
                          disabled={loading}
                          title="Edit"
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                      )}
                      {user.role !== 'admin' && (
                        <button
                          className={`btn btn-sm ${user.is_active ? 'btn-warning' : 'btn-success'}`}
                          onClick={() => handleToggleStatus(user)}
                          disabled={loading}
                          title={user.is_active ? 'Deactivate' : 'Activate'}
                        >
                          <i className={`fas fa-${user.is_active ? 'pause' : 'play'}`}></i>
                        </button>
                      )}
                      {user.role !== 'admin' && (
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDelete(user.id)}
                          disabled={loading}
                          title="Delete"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      )}
                      {user.role === 'admin' && (
                        <span className="admin-protected">
                          <i className="fas fa-shield-alt"></i>
                          Protected
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="table-footer">
        <div className="table-info">
          Showing {pagination.from} to {pagination.to} of {pagination.total} users
        </div>
        {renderPagination()}
      </div>
    </div>
  );
};

export default UserDataTable;

